import { useRef, useState, useEffect, useCallback } from 'react';

/**
 * Touch-friendly signature pad on HTML5 Canvas.
 *
 * Lightly adapted from frontend/src/components/SignaturePad.jsx — same
 * core logic (fingertip + mouse paths, scaled-down JPEG output, white
 * background for PDF embedding) but restyled for the mobile design
 * system: rounded-card border, mb-card-tap-style hint colour, taller
 * default canvas height tuned for thumb signatures.
 *
 * Outputs a base64 JPEG data URI (300x150 q=0.5, ~5KB) via `onChange`.
 * That's the format the backend stores in
 * `release_notes.receiver_signature` and rebroadcasts in the stamped
 * collection PDF.
 *
 *   <SignaturePad onChange={setSignature} height={180} />
 *
 * Touch handlers are attached imperatively with { passive: false } so
 * touchmove can call preventDefault() to stop the page from scrolling
 * while the user is signing — React's synthetic events default to
 * passive listeners and would let the page scroll under the finger.
 */
export default function SignaturePad({
  onChange,
  width = 400,
  height = 200,
  className = '',
}) {
  const canvasRef = useRef(null);
  const [drawing, setDrawing] = useState(false);
  const [hasContent, setHasContent] = useState(false);

  // Translate a touch/mouse event into canvas-local coordinates,
  // accounting for any CSS scaling (the canvas is `w-full` so the
  // bounding box rarely matches the intrinsic width/height attrs).
  const getPos = useCallback((e) => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;

    if (e.touches && e.touches.length > 0) {
      return {
        x: (e.touches[0].clientX - rect.left) * scaleX,
        y: (e.touches[0].clientY - rect.top) * scaleY,
      };
    }
    return {
      x: (e.clientX - rect.left) * scaleX,
      y: (e.clientY - rect.top) * scaleY,
    };
  }, []);

  const startDraw = useCallback((e) => {
    e.preventDefault();
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const pos = getPos(e);
    ctx.beginPath();
    ctx.moveTo(pos.x, pos.y);
    setDrawing(true);
  }, [getPos]);

  const draw = useCallback((e) => {
    if (!drawing) return;
    e.preventDefault();
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const pos = getPos(e);
    ctx.lineTo(pos.x, pos.y);
    ctx.stroke();
  }, [drawing, getPos]);

  // Compress on commit — full-resolution canvas → 300x150 JPEG q=0.5.
  // Backend stores the data URI verbatim and re-embeds in the collection
  // PDF, so a smaller payload means a smaller PDF + faster API roundtrip.
  // White background is required because JPEG has no transparency.
  const exportSignature = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas || !onChange) return;
    const outW = 300;
    const outH = 150;
    const out = document.createElement('canvas');
    out.width = outW;
    out.height = outH;
    const octx = out.getContext('2d');
    octx.fillStyle = '#ffffff';
    octx.fillRect(0, 0, outW, outH);
    octx.drawImage(canvas, 0, 0, outW, outH);
    onChange(out.toDataURL('image/jpeg', 0.5));
  }, [onChange]);

  const endDraw = useCallback(() => {
    if (!drawing) return;
    setDrawing(false);
    setHasContent(true);
    exportSignature();
  }, [drawing, exportSignature]);

  // Set up the drawing context once (stroke style etc).
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    ctx.strokeStyle = '#0f172a'; // slate-900 — high-contrast for PDF print
    ctx.lineWidth = 2.5;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
  }, []);

  // Imperative touch listeners with { passive: false } so we can
  // preventDefault on touchmove (otherwise the page scrolls under the
  // finger while signing).
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const opts = { passive: false };
    canvas.addEventListener('touchstart', startDraw, opts);
    canvas.addEventListener('touchmove', draw, opts);
    canvas.addEventListener('touchend', endDraw);
    canvas.addEventListener('touchcancel', endDraw);

    return () => {
      canvas.removeEventListener('touchstart', startDraw);
      canvas.removeEventListener('touchmove', draw);
      canvas.removeEventListener('touchend', endDraw);
      canvas.removeEventListener('touchcancel', endDraw);
    };
  }, [startDraw, draw, endDraw]);

  const clear = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setHasContent(false);
    if (onChange) onChange(null);
  };

  return (
    <div className={className}>
      <div className="border-2 border-dashed border-gray-300 rounded-card bg-white relative">
        <canvas
          ref={canvasRef}
          width={width}
          height={height}
          className="w-full cursor-crosshair touch-none rounded-card"
          style={{ aspectRatio: `${width}/${height}` }}
          onMouseDown={startDraw}
          onMouseMove={draw}
          onMouseUp={endDraw}
          onMouseLeave={endDraw}
        />
        {!hasContent && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none text-gray-400 text-small">
            Sign here with your finger
          </div>
        )}
      </div>
      {hasContent && (
        <button
          type="button"
          onClick={clear}
          className="mt-2 text-small text-danger min-h-touch px-1"
        >
          Clear signature
        </button>
      )}
    </div>
  );
}
